import { useQuery } from 'react-query'

import cateGraphQLClient from '@/graphql/cate/client'
import { Listings_Detail, PotentialPropertiesBySuggestedCriteriaQuery } from '@/types/cate-graphql-generated-types'
import { nikaListingsToLegacyListings } from '@/utils/nika/listing-mapper'
import { legacyGeoFilterToNikaFilter } from '@/utils/nika/query-mapper'
import { MAX_PAGE_SIZE, reqListings } from 'api/api-nika'

export type SuggestedPotentialProperties = PotentialPropertiesBySuggestedCriteriaQuery['potential_property']

export type ListingsAndPrioritiesBySuggestedCriteria = {
  listings: Listings_Detail[]
  potentialProperties: SuggestedPotentialProperties
}

/**
 *
 * @param geoSlugs
 * @param priceMax in EUR, i.e. 125_000 for 125.000,00€
 * @param roomsMin
 */
async function fetchListingsAndPrioritiesBySuggestedCriteria(
  geoSlugs: string[],
  priceMax: number,
  roomsMin: number
): Promise<ListingsAndPrioritiesBySuggestedCriteria> {
  // get listings from nika public
  const where = {
    pricing_sell_value: { $lte: priceMax }, // prices on nika are in EUR
    property_rooms_all: { $gte: roomsMin },
  }
  const nikaFilter = legacyGeoFilterToNikaFilter(geoSlugs, where)
  const { data: listings } = await reqListings(nikaFilter, { page: 1, page_size: MAX_PAGE_SIZE }, ['-ranking'])

  const potentialProperties = await cateGraphQLClient.potentialPropertiesBySuggestedCriteria({
    geoSlugs,
    priceMax: priceMax * 100, // prices on cate are in EUR cents
    roomsMin,
  })

  return {
    listings: listings?.data.map(nikaListingsToLegacyListings),
    potentialProperties: potentialProperties.data.potential_property,
  }
}

/**
 *
 * @param geoSlugs
 * @param priceMax in EUR, i.e. 125_000 for 125.000,00€
 * @param roomsMin
 */
export function useListingsAndPrioritiesBySuggestedCriteria({
  geoSlugs,
  priceMax,
  roomsMin,
}: {
  geoSlugs: string[]
  priceMax: number
  roomsMin: number
}) {
  return useQuery(['listings-and-priorities-by-suggested-criteria', geoSlugs, priceMax, roomsMin], () =>
    fetchListingsAndPrioritiesBySuggestedCriteria(geoSlugs, priceMax, roomsMin)
  )
}
